import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {TableColumn, TableSettings} from "../../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../../common/AbstractListView";
import * as XCol from '../../../../framework.core/XExportColumn';
import constants from '../../../../constants';
import {Search} from "../../../../filtering";
import {NavigationType, openNewPage} from '../../../../util/navigationHelpers';
import {
  DateRenderer,DateTimeIncludeSecRenderer,FloatRenderer,TextAlignLeftRenderer,TextRenderer
} from '../../../../uiControls/SmartTableRenderers';
import ApplicationContext from '../../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../../common/JobListViewContext';
import {ActionButtonItem, borderColors, splitMenuItemsIntoSections} from '../../../../ui/ActionButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import {JobNames} from "../../../../framework.core/JobNames";
import {salesCostBatchRecogList} from "../tableSettings";
import ListViewShortcutMenu from '../../../common/ListViewShortcutMenu';
import ToolboxMenuItem from '../../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../../ui/menu/Menu';
import { FmsSecTask } from '../../../../fms.core/constants';
import { findSecurityTask } from '../../../../util';
import { BatchRecogListItem } from '../../../../fms.data/models/BatchRecogListItem';
import { Decimalformat } from '../../../common/commonExportFormat';
import { ViewIconButton, ViewIconButton2 } from '../../../../ui/IconButton';
import Select from '../../../../ui/Select';

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();
  const selectedElement = jobListContext.selectedElement as BatchRecogListItem;

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const viewUrl = `/accounting/acSalesCostRecog/view/${id}`;
    openNewPage(history, viewUrl, NavigationType.newPage);
  }

  return {view};
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.SCRecogBatchRecog);

  const items = [
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantRead },
    { label: 'Search', shortcut: 'F5', separator: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="batchRecog-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        view();
        break;
      case 1:
        jobListContext.triggerTableSearch('F5');
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}


function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as BatchRecogListItem;

  const { view } = useSelectedOperations();
  const appContext = useContext(ApplicationContext);
  const hasSelectedRow = !!selectedElement;

  return (
     <div className='ifms-common-no-vertical-separator-gap-margin'>
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ?  <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}/> : <></>} */}
      <ViewIconButton2 name='receiving-list-view-button' disabled={!hasSelectedRow} onClick={view} useText  />
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});
  const {view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {
    } else if (unprocessedFkey === "F9") {
      view();
    } 

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F6', 'F9'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }


  return null;
}

function PeriodTypeFilterRenderer({value, onChange}:any) {
  return (
    <Select
      name='ifms-filter-periodType-select'
      value={value}
      onChange={e => onChange(e.target.value)}
      items={[
        { value: '', label: 'ALL'},
        { value: 'Recog Date', label: 'Recog Date'},
        { value: 'ETD/ETA', label: 'ETD/ETA'},
        { value: 'ETD/F.ETA', label: 'ETD/F.ETA'},
      ]}
    />
  );
}

/**
 * Concrete implementation for Other Shipment List screen
 *
 * @class ListView
 * @extends {AbstractListView}
 */
class ListView extends AbstractListView {

  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.BatchRecog,
      'BatchRecogListItem',
      BatchRecogListItem.calculateSearchParameters(search, BatchRecogListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
      );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: BatchRecogListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
      const apiResult = await api.getListItems(JobNames.BatchRecog, 'BatchRecogListItem',
      BatchRecogListItem.calculateSearchParameters(search,BatchRecogListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  BatchRecogListItem.formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'periodType',
        renderer: PeriodTypeFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {

    return BatchRecogListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeIncludeSecRenderer;
      else if(x.type === XCol.XCellType.Double) {
        if(x.dataIndex === 'estMargin'){
          colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={2} />;
        }else {
          colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={0} />;
        }

      }
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    colRenderer.push({accessor: 'estMargin', changedValue: (value: any)=> { return Decimalformat(value['estMargin'], 2)}});
    colRenderer.push({accessor: 'recogCount', changedValue: (value: any)=> { return Decimalformat(value['recogCount'], 0)}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'actionDate',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  salesCostBatchRecogList;
    const cols = BatchRecogListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: BatchRecogListItem) {
    return row && row.id ? `/accounting/acSalesCostRecog/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Batch Recog History';
  };

  resizetitleLabel(): boolean{
    return true;
  }
  
  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }

}

export default ListView;
